import { useCallback, useMemo, useState } from 'react';
import { searchPlaceInvoiceService as defaultService } from '../services/searchPlaceInvoiceService';
import { useGetSearchPlaceInvoice } from './useGetSearchPlaceInvoice';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

// ─── Pure Logic ───────────────────────────────────────────────────────────────

export const toInvoiceKey = inv => `${inv.placeId}_${inv.audit.createdAt}`;

export const buildAllPlacesParams = (userId, placeId = null) => ({
  userId,
  from: 0,
  size: PAGE_SIZE,
  filter: placeId ? { placeIds: [placeId] } : {},
});

export const buildPlaceInvoicesParams = (userId, placeId) =>
  placeId
    ? { userId, from: 0, size: PAGE_SIZE, filter: { placeIds: [placeId] } }
    : null;

/**
 * Deduplicates the all-places fetch into one card per unique place.
 * No client-side filtering — PlaceGate owns its own live search dropdown.
 */
export const derivePlaceGateState = ({ placeInvoicePage }) => {
  const allInvoices = placeInvoicePage?.placeInvoices ?? [];
  const seenIds = new Set();
  const places = allInvoices.filter(inv => {
    if (seenIds.has(inv.placeId)) return false;
    seenIds.add(inv.placeId);
    return true;
  });
  return { places };
};

/**
 * Derives the PaymentsView display state from the place-filtered fetch.
 * Pure — no side effects, no React.
 */
export const derivePlaceDetailState = ({
  placeInvoicePage,
  selectedInvoiceKey,
  selectedOrderId,
  invoiceSearch,
  orderSearch,
}) => {
  const allInvoices = placeInvoicePage?.placeInvoices ?? [];

  const selectedPlace = allInvoices[0] ?? null;

  const lowerInvoice = invoiceSearch.toLowerCase();
  const filteredInvoices = lowerInvoice
    ? allInvoices.filter(
        inv =>
          inv.name.toLowerCase().includes(lowerInvoice) ||
          (inv.audit.createdAt ?? '').toLowerCase().includes(lowerInvoice),
      )
    : allInvoices;

  const selectedInvoice = (() => {
    if (!allInvoices.length) return null;
    if (selectedInvoiceKey) {
      return (
        allInvoices.find(inv => toInvoiceKey(inv) === selectedInvoiceKey) ??
        allInvoices[0]
      );
    }
    return allInvoices[0];
  })();

  const invoiceOrders = selectedInvoice?.orders ?? [];
  const lowerOrder = orderSearch.toLowerCase();
  const filteredOrders = lowerOrder
    ? invoiceOrders.filter(
        o =>
          o.menuOrderId.toLowerCase().includes(lowerOrder) ||
          o.orderReference.toLowerCase().includes(lowerOrder) ||
          o.serviceType.toLowerCase().includes(lowerOrder) ||
          (o.orderDate ?? '').toLowerCase().includes(lowerOrder),
      )
    : invoiceOrders;

  const selectedOrder = selectedOrderId
    ? invoiceOrders.find(o => o.menuOrderId === selectedOrderId) ?? null
    : null;

  return {
    selectedPlace,
    filteredInvoices,
    selectedInvoice,
    filteredOrders,
    selectedOrder,
    totalPages: placeInvoicePage?.totalPages ?? 0,
    totalPlaces: placeInvoicePage?.totalPlaces ?? 0,
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePlaceInvoices(userId, options = {}, service = defaultService) {
  const { initialPlaceId = null } = options;

  // ── Selection state ───────────────────────────────────────────────────────
  // selectedPlaceId: set by card click — also updates the route.
  // dropdownPlaceId: set by dropdown suggestion — no route update.
  // activePlaceId: whichever is set, drives fetch 2.
  const [selectedPlaceId, setSelectedPlaceId] = useState(initialPlaceId);
  const [dropdownPlaceId, setDropdownPlaceId] = useState(null);
  const [selectedInvoiceKey, setSelectedInvoiceKey] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const activePlaceId = selectedPlaceId;

  // ── Client-side search strings ────────────────────────────────────────────
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');

  // ── Fetch 1: all places (PlaceGate cards) ─────────────────────────────────
  const allPlacesParams = useMemo(
    () => buildAllPlacesParams(userId, dropdownPlaceId),
    [userId, dropdownPlaceId],
  );

  const {
    status: allPlacesStatus,
    placeInvoicePage: allPlacesPage,
    error: allPlacesError,
    refresh: refreshAllPlaces,
  } = useGetSearchPlaceInvoice(allPlacesParams, service);

  // ── Fetch 2: active place invoices (PaymentsView) ─────────────────────────
  const placeInvoicesParams = useMemo(
    () => buildPlaceInvoicesParams(userId, activePlaceId),
    [userId, activePlaceId],
  );

  const {
    status: placeInvoicesStatus,
    placeInvoicePage: placeInvoicesPage,
    error: placeInvoicesError,
    refresh: refreshPlaceInvoices,
  } = useGetSearchPlaceInvoice(placeInvoicesParams, service);

  // ── Derived state ─────────────────────────────────────────────────────────
  const gateDerived = useMemo(
    () => derivePlaceGateState({ placeInvoicePage: allPlacesPage }),
    [allPlacesPage],
  );

  const detailDerived = useMemo(
    () =>
      derivePlaceDetailState({
        placeInvoicePage: placeInvoicesPage,
        selectedInvoiceKey,
        selectedOrderId,
        invoiceSearch,
        orderSearch,
      }),
    [placeInvoicesPage, selectedInvoiceKey, selectedOrderId, invoiceSearch, orderSearch],
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  // Card click — also triggers a route update in the caller.
  const selectPlace = useCallback(placeId => {
    setSelectedPlaceId(placeId);
    setDropdownPlaceId(null);
    setSelectedInvoiceKey(null);
    setSelectedOrderId(null);
    setInvoiceSearch('');
    setOrderSearch('');
  }, []);

  // Dropdown suggestion — drives the fetch without touching the route.
  const selectDropdownPlace = useCallback(placeId => {
    setDropdownPlaceId(placeId);
    setSelectedInvoiceKey(null);
    setSelectedOrderId(null);
    setInvoiceSearch('');
    setOrderSearch('');
  }, []);

  const changePlace = useCallback(() => {
    setSelectedPlaceId(null);
    setDropdownPlaceId(null);
    setSelectedInvoiceKey(null);
    setSelectedOrderId(null);
  }, []);

  const selectInvoice = useCallback(key => {
    setSelectedInvoiceKey(key);
    setSelectedOrderId(null);
    setOrderSearch('');
  }, []);

  const selectOrder = useCallback(menuOrderId => {
    setSelectedOrderId(menuOrderId);
  }, []);

  const closeOrder = useCallback(() => {
    setSelectedOrderId(null);
  }, []);

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    // ── First screen (PlaceGate) ──────────────────────────────────────────────
    allPlacesStatus,
    allPlacesError,
    places: gateDerived.places,
    refreshAllPlaces,

    // ── Transition ────────────────────────────────────────────────────────────
    // hasPlace tracks only card-click selection — dropdown prefetches data but
    // does not transition the user to the detail screen.
    hasPlace: selectedPlaceId !== null,
    placeInvoicesStatus,
    isReadyForDetails: placeInvoicesStatus === 'success',
    placeInvoicesError,
    refreshPlaceInvoices,

    // ── Second screen (PaymentsView) ──────────────────────────────────────────
    selectedPlace: detailDerived.selectedPlace,
    selectPlace,
    selectDropdownPlace,
    changePlace,

    filteredInvoices: detailDerived.filteredInvoices,
    invoiceSearch,
    setInvoiceSearch,
    selectedInvoice: detailDerived.selectedInvoice,
    selectInvoice,
    toInvoiceKey,

    filteredOrders: detailDerived.filteredOrders,
    orderSearch,
    setOrderSearch,
    selectedOrder: detailDerived.selectedOrder,
    selectOrder,
    closeOrder,
  };
}
